import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

// Auth Pages
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  OtpPage,
  OnboardPage,
  ResetPasswordPage,
} from '../features/auth';
import {
  AgentLoginPage,
  AgentDashboardPage,
  AgentLiveChatPage,
  AgentProfilePage,
} from '../features/agent';
import {
  AgentAdminLoginPage,
  AgentAdminDashboardPage,
  AgentAdminLiveChatPage,
  AgentAdminProfilePage,
  AgentDetailsPage,
} from '../features/agent-admin';

// Dashboard Pages
import { DashboardPage, ReportPage as DashboardReportPage } from '../features/dashboard';


// Notification Pages
import { NotificationsPage } from '../features/notifications';


// Bots Pages
import {
  BotsPage,
  BotBuilderPage,
  BotSettingsPage,
  BotPublishPage,
  BotMediaPage,
  BotMetaMediaPage,
} from '../features/bots';

// Broadcast Pages
import {
  BroadcastPage,
  BroadcastHistoryPage,
  BroadcastSchedulePage,
  BroadcastCreatePage,
  BroadcastDetailPage,
  BroadcastGroupsPage,
} from '../features/broadcast';

// Customer Pages
import {
  CustomersPage,
  CustomerDetailPage,
  CustomerCreatePage,
  CustomerImportPage,
} from '../features/customers';

// Message Pages
import {
  MessagesPage,
  MessageDetailPage,
  ServiceMessagesPage,
  BusinessMessagesPage,
  InboxManagementPage,
} from '../features/messages';

// Template Pages
import {
  TemplatesPage,
  TemplateCreatePage,
  TemplateEditPage,
  TemplateDraftsPage,
  TemplateStatusPage,
  TemplateViewPage,
} from '../features/templates';

// Live Chat Pages
import { LiveChatPage } from '../features/live-chat';

// Calendar Pages
import { CalendarPage } from '../features/calendar';

// Appointment Pages
import {
  AppointmentsPage,
  AppointmentDetailPage,
  AppointmentFlowPage,
  BranchesPage,
  EmployeesPage,
  ServicesPage,
} from '../features/appointments';

// Order Pages
import { OrdersPage, OrderDetailPage } from '../features/orders';

// Catalog Pages
import {
  CatalogPage,
  ProductDetailPage,
  ProductCreatePage,
  ProductEditPage,
} from '../features/catalog';

// FAQ Pages
import { FaqPage } from '../features/faq';

// Report Pages
import ReportsPage from '../features/reports/pages/ReportsPage';

// Settings Pages
import {
  SettingsPage,
  ProfilePage,
  WebhookPage,
  ApiTokenPage,
  TemplateApiDocsPage,
  WhatsAppProfilePage,
  FacebookDetailsPage,
  BotMessagesPage,
} from '../features/settings';

// Admin Pages
import {
  UsersPage,
  UserDetailPage,
  UserCreatePage,
  ClientsPage,
  PricingPage,
  AdminDashboardPage,
} from '../features/admin';

// Layout
import { DashboardLayout } from '../layouts';
import NotFound from './NotFound';
import { ProtectedRoute, PublicRoute } from './RouteGuards';
import Unauthorized from './Unauthorized';
import RoleLanding from './RoleLanding';

const AppRoutes = ({ session = {}, translation = {}, lang = 'en', isLoading = false }) => {
  const location = useLocation();
  const layoutProps = { session, translation, lang };

  const renderProtected = (Component, componentProps = {}, allowedRoles = []) => (
    <ProtectedRoute session={session} isLoading={isLoading} allowedRoles={allowedRoles}>
      <DashboardLayout {...layoutProps}>
        <Component
          session={session}
          translation={translation}
          {...componentProps}
        />
      </DashboardLayout>
    </ProtectedRoute>
  );

  const renderPublic = (Component, componentProps = {}) => (
    <PublicRoute session={session} isLoading={isLoading}>
      <Component translation={translation} lang={lang} {...componentProps} />
    </PublicRoute>
  );

  return (
    <Routes location={location} key={location.pathname}>
      {/* Public Routes (Auth) */}
      <Route path="/login" element={renderPublic(LoginPage)} />
      <Route path="/agent/login" element={renderPublic(AgentLoginPage)} />
      <Route path="/agent-admin/login" element={renderPublic(AgentAdminLoginPage)} />
      <Route path="/register" element={renderPublic(RegisterPage)} />
      <Route path="/forgot-password" element={renderPublic(ForgotPasswordPage)} />
      <Route path="/reset-password" element={renderPublic(ResetPasswordPage)} />
      <Route path="/otp" element={renderPublic(OtpPage)} />
      <Route path="/onboard" element={renderProtected(OnboardPage)} />

      {/* Protected Routes with Layout */}
      <Route path="/" element={renderProtected(RoleLanding, { fallback: DashboardPage })} />

      {/* Agent Routes */}
      <Route path="/agent" element={renderProtected(AgentDashboardPage, {}, ['agent'])} />
      <Route path="/agent/chat" element={renderProtected(AgentLiveChatPage, {}, ['agent'])} />
      <Route path="/agent/profile" element={renderProtected(AgentProfilePage, {}, ['agent'])} />

      {/* Agent Admin Routes */}
      <Route path="/agent-admin" element={renderProtected(AgentAdminDashboardPage, {}, ['agentAdmin', 'admin', 'superadmin'])} />
      <Route path="/agent-admin/chat" element={renderProtected(AgentAdminLiveChatPage, {}, ['agentAdmin', 'admin', 'superadmin'])} />
      <Route path="/agent-admin/profile" element={renderProtected(AgentAdminProfilePage, {}, ['agentAdmin', 'admin', 'superadmin'])} />
      <Route path="/agent-admin/agents" element={renderProtected(AgentDetailsPage, {}, ['agentAdmin', 'admin', 'superadmin'])} />

      {/* Bots Routes */}
      <Route path="/bots" element={renderProtected(BotsPage)} />
      <Route path="/botbuilder" element={renderProtected(BotBuilderPage)} />
      <Route path="/botsettings" element={renderProtected(BotSettingsPage)} />
      <Route path="/botpublish" element={renderProtected(BotPublishPage)} />
      <Route path="/botmedia" element={renderProtected(BotMediaPage)} />
      <Route path="/botmedia-meta" element={renderProtected(BotMetaMediaPage)} />

      {/* Broadcast Routes */}
      <Route path="/broadcast" element={renderProtected(BroadcastPage)} />
      <Route path="/broadcast/create" element={renderProtected(BroadcastCreatePage)} />
      <Route path="/broadcast/history" element={renderProtected(BroadcastHistoryPage)} />
      <Route path="/broadcast/schedule" element={renderProtected(BroadcastSchedulePage)} />
      <Route path="/broadcast/:id" element={renderProtected(BroadcastDetailPage)} />
      <Route path="/broadcast/detail/:id" element={renderProtected(BroadcastDetailPage)} />
      <Route path="/broadcast/groups" element={renderProtected(BroadcastGroupsPage)} />

      {/* Customer Routes */}
      <Route path="/customers" element={renderProtected(CustomersPage)} />
      <Route path="/customers/create" element={renderProtected(CustomerCreatePage)} />
      <Route path="/customers/import" element={renderProtected(CustomerImportPage)} />
      <Route path="/customers/:id" element={renderProtected(CustomerDetailPage)} />

      {/* Message Routes */}
      <Route path="/messages" element={renderProtected(MessagesPage)} />
      <Route path="/messages/service" element={renderProtected(ServiceMessagesPage)} />
      <Route path="/messages/business" element={renderProtected(BusinessMessagesPage)} />
      <Route path="/messages/:id" element={renderProtected(MessageDetailPage)} />
      <Route path="/inbox" element={renderProtected(InboxManagementPage)} />

      {/* Template Routes */}
      <Route path="/templates" element={renderProtected(TemplatesPage)} />
      <Route path="/templates/create" element={renderProtected(TemplateCreatePage)} />
      <Route path="/templates/drafts" element={renderProtected(TemplateDraftsPage)} />
      <Route path="/templates/status" element={renderProtected(TemplateStatusPage)} />
      <Route path="/templates/edit/:id" element={renderProtected(TemplateEditPage)} />
      <Route path="/templates/:id" element={renderProtected(TemplateViewPage)} />

      {/* Live Chat Routes */}
      <Route path="/chat" element={renderProtected(LiveChatPage)} />

      {/* Calendar Routes */}
      <Route path="/calendar" element={renderProtected(CalendarPage)} />

      {/* Appointment Routes */}
      <Route path="/appointments" element={renderProtected(AppointmentsPage)} />
      <Route path="/appointments/:id" element={renderProtected(AppointmentDetailPage)} />
      <Route path="/appointment-flow" element={renderProtected(AppointmentFlowPage)} />
      <Route path="/branches" element={renderProtected(BranchesPage)} />
      <Route path="/employees" element={renderProtected(EmployeesPage)} />
      <Route path="/appointment-services" element={renderProtected(ServicesPage)} />

      {/* Order Routes */}
      <Route path="/orders" element={renderProtected(OrdersPage)} />
      <Route path="/orders/:id" element={renderProtected(OrderDetailPage)} />

      {/* Catalog Routes */}
      <Route path="/catalog" element={renderProtected(CatalogPage)} />
      <Route path="/catalog/create" element={renderProtected(ProductCreatePage)} />
      <Route path="/catalog/:id" element={renderProtected(ProductDetailPage)} />
      <Route path="/catalog/edit/:id" element={renderProtected(ProductEditPage)} />

      {/* FAQ Routes */}
      <Route path="/faq" element={renderProtected(FaqPage)} />

      {/* Report Routes */}
      <Route path="/reports" element={renderProtected(ReportsPage)} />

      {/* Settings Routes */}
      <Route path="/settings" element={renderProtected(SettingsPage)} />
      <Route path="/settings/profile" element={renderProtected(ProfilePage)} />
      <Route path="/settings/webhook" element={renderProtected(WebhookPage)} />
      <Route path="/settings/api-token" element={renderProtected(ApiTokenPage)} />
      <Route path="/settings/template-api-docs" element={renderProtected(TemplateApiDocsPage)} />
      <Route path="/settings/bot-messages" element={renderProtected(BotMessagesPage)} />
      <Route path="/whatsappProfile" element={renderProtected(WhatsAppProfilePage)} />
      <Route path="/facebookDetails" element={renderProtected(FacebookDetailsPage)} />

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={renderProtected(AdminDashboardPage, {}, ['admin', 'superadmin'])} />
      <Route path="/admin/users" element={renderProtected(UsersPage)} />
      <Route path="/client/users" element={renderProtected(UsersPage)} />
      <Route path="/admin/users/create" element={renderProtected(UserCreatePage)} />
      <Route path="/admin/users/:id" element={renderProtected(UserDetailPage)} />
      <Route path="/admin/clients" element={renderProtected(ClientsPage)} />
      <Route path="/admin/pricing" element={renderProtected(PricingPage)} />
      <Route path="/history" element={renderProtected(DashboardReportPage)} />
      <Route path="/notifications" element={renderProtected(NotificationsPage)} />
      <Route path="/unauthorized" element={renderProtected(Unauthorized)} />


      {/* 404 Page */}
      <Route
        path="/404"
        element={
          session && session.uid ? (
            <ProtectedRoute session={session} isLoading={isLoading}>
              <DashboardLayout {...layoutProps}>
                <NotFound session={session} />
              </DashboardLayout>
            </ProtectedRoute>
          ) : (
            <PublicRoute session={session} isLoading={isLoading}>
              <NotFound session={session} />
            </PublicRoute>
          )
        }
      />

      {/* Catch all - show 404 */}
      <Route
        path="*"
        element={
          session && session.uid ? (
            <ProtectedRoute session={session} isLoading={isLoading}>
              <DashboardLayout {...layoutProps}>
                <NotFound session={session} />
              </DashboardLayout>
            </ProtectedRoute>
          ) : (
            <PublicRoute session={session} isLoading={isLoading}>
              <NotFound session={session} />
            </PublicRoute>
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
